import React, { useState } from 'react';
import { Text, StyleSheet, View } from 'react-native';
import GenericButton from '../../../../components/molecules/buttons/GenericButton';
import RevokeConfirmModal from '../../../../modal/RevokeConfirmModal';
import { useIssuerService } from '../../../../services';

interface ICredentialItemProps {
  credential: any;
}

/**
 * CredentialItem component
 *
 * @param {*} { credential }
 * @return {*}
 */
const CredentialItem: React.FC<ICredentialItemProps> = ({ credential }) => {
  const [revokeModalVisible, setRevokeModalVisible] = useState(false);
  const issuerService = useIssuerService();

  const openRevokeModal = () => {
    setRevokeModalVisible(true);
  };

  const closeRevokeModal = () => {
    setRevokeModalVisible(false);
  };

  const revokeCredential = async () => {
    try {
      await issuerService.revokeCredential(
        credential.connectionId,
        credential.credRevId,
        credential.revRegId,
        credential.threadId
      );
    } catch (e) {
      console.log(e);
    }
  };

  const getStatusColor = (state: string) => {
    switch (state) {
      case 'offer-sent':
        return '#ffc107';
      case 'done':
        return '#28a745';
      case 'abandoned':
      case 'credential-revoked':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getStatusText = (state: string) => {
    switch (state) {
      case 'offer-sent':
        return 'Pending';
      case 'done':
        return 'Issued';
      case 'abandoned':
        return 'Abandoned';
      case 'credential-revoked':
        return 'Revoked';
      default:
        return state;
    }
  };

  return (
    <>
      <RevokeConfirmModal
        isVisible={revokeModalVisible}
        onClose={closeRevokeModal}
        onConfirm={revokeCredential}
        credentialName={credential.name}
      />

      <View style={styles.credentialCard} key={`${credential.credential_id}`}>
        <View style={styles.credentialHeader}>
          <View style={styles.credentialInfo}>
            <Text style={styles.credentialName}>{credential.name}</Text>
            <View style={styles.statusContainer}>
              <Text style={styles.statusLabel}>Status: </Text>
              <Text style={[styles.statusText, { color: getStatusColor(credential.state) }]}>
                {getStatusText(credential.state)}
              </Text>
            </View>
          </View>
          {credential.credRevId && credential.state === 'done' && (
            <GenericButton
              title={'Revoke'}
              onPress={openRevokeModal}
              buttonStyle={styles.revokeButton}
              textStyle={styles.buttonText}
              isPrimary={false}
            />
          )}
        </View>

        <View style={styles.detailsSection}>
          <Text style={styles.detailsTitle}>Credential Details:</Text>
          {credential.attributes.map((attribute: any, index: number) => (
            <View style={styles.attributeRow} key={`${credential.credential_id}-${index}`}>
              <Text style={styles.attributeLabel}>{attribute.name}:</Text>
              <Text style={styles.attributeValue}>{attribute.value}</Text>
            </View>
          ))}
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  credentialCard: {
    backgroundColor: '#f1f2f1',
    borderRadius: 8,
    marginVertical: 8,
    padding: 16,
    borderLeftWidth: 4,
    borderLeftColor: '#007bff',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1
    },
    width: '90%',
    alignSelf: 'center',
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2
  },
  credentialHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12
  },
  credentialInfo: {
    flex: 1,
    marginRight: 12
  },
  credentialName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#212529',
    marginBottom: 4
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  statusLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6c757d'
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600'
  },
  detailsSection: {
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    paddingTop: 12
  },
  detailsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#495057',
    marginBottom: 8
  },
  attributeRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    paddingLeft: 16
  },
  attributeLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6c757d',
    minWidth: 120
  },
  attributeValue: {
    fontSize: 14,
    fontWeight: '400',
    color: '#212529',
    flex: 1
  },
  revokeButton: {
    width: 80,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#dc3545'
  },
  buttonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#ffffff'
  }
});

export default CredentialItem;
